import * as React from 'react';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Device,
  FormattedText,
  Icon,
  IconName,
  Typography,
  TypographyVariant,
  defaultHtmlMapping,
  useDeviceViewport,
  useTheme,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useTranslations } from '@utils/useTranslations';
import { createPortal } from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { SalePageModal } from '@features/Sale/SalePageModal/SalePageModal';
import {
  BaseDetailsProps,
  ControlledDetailsProps,
  DetailsContainerProps,
  DetailsSectionProps,
  OverlayHeaderProps,
  UncontrolledDetailsProps,
} from './Details.interfaces';
import {
  LabelWrapper,
  OverlayHeaderContainer,
  StyledDetailsContainer,
  StyledHeader,
  StyledTypography,
  TextContainer,
  UnderlineTypography,
} from './Details.styles';

export const OVERLAY_HEADER_BUTTON_TEST_ID = 'OverlayHeaderContainerButton';

export const DETAILS_SECTION_HEADER_TEST_ID = 'details-section-header';

/**
 * This component handles its own open state for Details section
 **/
export const UncontrolledDetails: React.FunctionComponent<UncontrolledDetailsProps> = props => {
  const [isOpen, setIsOpen] = React.useState(false);

  const onChange: (event: React.MouseEvent) => void = e => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <BaseDetails
      {...props}
      onClick={onChange}
      onBackClick={onChange}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
};

/**
 * This component has its open state controlled by parent component
 **/
export const ControlledDetails: React.FunctionComponent<ControlledDetailsProps> = props => {
  const onChange: (event: React.MouseEvent) => void = e => {
    e.preventDefault();
    props.setIsOpen(!props.isOpen);
  };

  return <BaseDetails {...props} onClick={onChange} onBackClick={onChange} />;
};

/**
 * This is a duplicate of the Cauldron component (SalePageDetails), with this ticket: https://secretescapes.atlassian.net/browse/DEV-79467. the Cauldron component would be removed
 * All references for details in Bullfinch will be using this component and not the Cauldron component
 **/
export const BaseDetails: React.FunctionComponent<BaseDetailsProps> = ({
  onClick,
  onBackClick,
  labelIcon,
  buttonLabel,
  withPortal,
  isOpen,
  setIsOpen,
  content,
  popupTitle,
}) => {
  const deviceViewport: Device = useDeviceViewport();
  const { color } = useTheme();

  return (
    <>
      <LabelWrapper onClick={onClick}>
        {labelIcon && <Icon name={labelIcon} fillColor={color.blackText} />}
        <UnderlineTypography
          variant={labelIcon ? TypographyVariant.link : TypographyVariant.linkSecondary}
          component="a"
          componentProps={{ href: 'javascript:void(0)' }}
        >
          {buttonLabel}
        </UnderlineTypography>
      </LabelWrapper>
      {deviceViewport === Device.mobile ? (
        withPortal ? (
          createPortal(
            <DetailsContainer
              isOpen={isOpen}
              onBackClick={onBackClick}
              content={content}
              popUpTitle={popupTitle}
            />,
            document.body
          )
        ) : (
          <DetailsContainer
            isOpen={isOpen}
            onBackClick={onBackClick}
            content={content}
            popUpTitle={popupTitle}
          />
        )
      ) : (
        <SalePageModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          content={content}
          modalTitle={popupTitle}
        />
      )}
    </>
  );
};

/**
 * This is a duplicate of the Cauldron component (SalePageDetailsSection), with this ticket: https://secretescapes.atlassian.net/browse/DEV-79467. the Cauldron component would be removed
 * All references for details in Bullfinch will be using this component and not the Cauldron component
 **/
export const DetailsSection: React.FunctionComponent<DetailsSectionProps> = ({
  header,
  description,
  id,
}) => {
  const theme = useTheme();

  if (!description) {
    return null;
  }

  return (
    <div data-testid={id}>
      {header ? (
        <StyledHeader data-testid={DETAILS_SECTION_HEADER_TEST_ID}>
          <Typography variant={TypographyVariant.bodyBold}>{header}</Typography>
        </StyledHeader>
      ) : null}
      <FormattedText
        html={description}
        fontColor={theme.color.blackText}
        htmlMapping={{
          ...defaultHtmlMapping,
          a: TypographyVariant.linkSecondary,
        }}
      />
    </div>
  );
};

/**
 * This is a duplicate of the Cauldron component (SalePageOverlayHeader), with this ticket: https://secretescapes.atlassian.net/browse/DEV-79467. the Cauldron component would be removed
 * All references for details in Bullfinch will be using this component and not the Cauldron component
 **/
export const OverlayHeader: React.FunctionComponent<OverlayHeaderProps> = (
  props: OverlayHeaderProps
) => {
  const theme = useTheme();
  const { translations } = useTranslations();
  const { onBackClick, title } = props;

  return (
    <OverlayHeaderContainer>
      <Button
        leftIcon={IconName.chevronLeft}
        size={ButtonSize.medium}
        onClick={onBackClick}
        dataAttributes={{ testid: OVERLAY_HEADER_BUTTON_TEST_ID }}
        variant={ButtonVariant.textWhite}
      >
        {i18n('mobile.backButton', [], translations)}
      </Button>
      <StyledTypography color={theme.color.white} variant={TypographyVariant.bodyBold}>
        {title}
      </StyledTypography>
    </OverlayHeaderContainer>
  );
};

export const DetailsContainer = ({
  content,
  onBackClick,
  isOpen,
  popUpTitle,
}: DetailsContainerProps) => {
  return (
    <RemoveScroll enabled={isOpen} forwardProps>
      <StyledDetailsContainer isOpen={isOpen} data-testid={`facilities-overlay-active-${isOpen}`}>
        <OverlayHeader title={popUpTitle} onBackClick={onBackClick} />
        <TextContainer>
          {typeof content === 'string' ? (
            <FormattedText
              html={content}
              htmlMapping={{
                ...defaultHtmlMapping,
                a: TypographyVariant.link,
              }}
            />
          ) : (
            content
          )}
        </TextContainer>
      </StyledDetailsContainer>
    </RemoveScroll>
  );
};
